import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationContainer, NotificationManager } from "react-notifications";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { APP_STEPS } from "../config/steps";
import "./PersonalInfo.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const PersonalInformation = () => {
  // ---- state (existing field names kept) ----
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(false);
  const [applicationid, setApplicationId] = useState();
  const [errors, setErrors] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [deathCert, setDeathCert] = useState(null);
  const [deathCertPreview, setDeathCertPreview] = useState(null);
  const [frcFile, setFrcFile] = useState(null);
  const [frcPreview, setFrcPreview] = useState(null);
  const [frcText, setFrcText] = useState("");
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";
  const navigate = useNavigate();

  const currentStep = 0; // index in APP_STEPS

  // ---- fetch existing application data ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const campaignRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const campaignResponse = await campaignRes.json();
        const latestCampaign = campaignResponse.data[0];
        if (!latestCampaign) { setLoading(false); return; }

        const latestDocumentId = latestCampaign.documentId;
        const userRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/users/me?populate[applications][filters][campaign][documentId][$eq]=${latestDocumentId}&populate[applications][populate][1]=profile_picture&populate[applications][populate][2]=death_certificate&populate[applications][populate][3]=proof_of_relationship`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const user = await userRes.json();
        const applications = user.applications;

        if (applications.length > 0) {
          setApplication(true);
          setFormData(applications[0]);
          setApplicationId(applications[0].id);
          localStorage.setItem("id", applications[0].id);
          if (applications[0].profile_picture?.url) {
            let url = applications[0].profile_picture.url.replace("/api", "");
            setProfileImagePreview(url);
          }
          if (applications[0].death_certificate?.url) {
            let dcUrl = applications[0].death_certificate.url.replace("/api", "");
            setDeathCertPreview(applications[0].death_certificate.mime?.startsWith("application") ? "pdf" : dcUrl);
          }
          if (applications[0].proof_of_relationship?.url) {
            let frcUrl = applications[0].proof_of_relationship.url.replace("/api", "");
            setFrcPreview(applications[0].proof_of_relationship.mime?.startsWith("application") ? "pdf" : frcUrl);
          }
          if (applications[0].relationship_detail) {
            setFrcText(applications[0].relationship_detail);
          }
        } else {
          const info = JSON.parse(localStorage.getItem("auth_user_info"));
          setFormData({ name: info.fname, email: info.email1 });
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // ---- image handling ----
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type === "image/jpeg" || file.type === "image/png") {
      if (file.size <= 5000000) {
        setProfileImage(file);
        setProfileImagePreview(URL.createObjectURL(file));
      } else {
        NotificationManager.error("File size must be less than 5MB");
      }
    } else {
      NotificationManager.error("Only JPEG and PNG formats are allowed");
    }
  };

  // ---- father_alive is a real boolean field in Strapi ----
  // Alive -> the supporter is the father himself; deceased -> the applicant picks
  // one from the dropdown, so the stored value is reset to match the visible select.
  const handleFatherAliveChange = (alive) => {
    setFormData((prev) => {
      const next = { ...prev, father_alive: alive };
      next.primary_supporter = alive ? "father" : "";
      if (alive) {
        next.relationship_detail = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, father_alive: "" }));
    if (alive) {
      setDeathCert(null);
      setDeathCertPreview(null);
      setFrcFile(null);
      setFrcPreview(null);
      setFrcText("");
    }
  };

  // ---- clear FRC when primary_supporter changes away from guardian_relative ----
  useEffect(() => {
    if (formData.father_alive === false && formData.primary_supporter !== "guardian_relative") {
      if (frcFile || frcPreview) {
        setFrcFile(null);
        setFrcPreview(null);
      }
      setFrcText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.primary_supporter]);

  // ---- FRC (proof of relationship) handling ----
  const handleFrcChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      NotificationManager.error("Only JPEG, PNG, and PDF formats are allowed");
      return;
    }
    if (file.size <= 5000000) {
      setFrcFile(file);
      setFrcPreview(file.type === "application/pdf" ? "pdf" : URL.createObjectURL(file));
    } else {
      NotificationManager.error("File size must be less than 5 MB");
    }
  };

  // ---- death certificate handling ----
  const handleDeathCertChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      NotificationManager.error("Only JPEG, PNG, and PDF formats are allowed");
      return;
    }
    if (file.size <= 5000000) {
      setDeathCert(file);
      setDeathCertPreview(file.type === "application/pdf" ? "pdf" : URL.createObjectURL(file));
    } else {
      NotificationManager.error("File size must be less than 5 MB");
    }
  };

  // ---- input change ----
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const errorMessage = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: errorMessage || "" }));
  };

  // ---- field validation (existing rules kept) ----
  const validateField = (name, value) => {
    switch (name) {
      case "phone_number":
        if (value && !/^(\+923|03)\d{9}$/.test(value)) return "Phone number is invalid.";
        break;
      case "email":
        if (value && !/\S+@\S+\.\S+/.test(value)) return "Email is invalid.";
        break;
      case "applicant_cnic":
        if (value && !/^\d{13}$/.test(value)) return "CNIC is invalid. It should be a 13-digit number.";
        break;
      case "parent_cnic":
        if (value && !/^\d{13}$/.test(value)) return "CNIC is invalid. It should be a 13-digit number.";
        break;
      default:
        return null;
    }
  };

  // ---- full form validation (existing rules kept) ----
  const validateForm = () => {
    const newErrors = {};
    const calculateAge = (dateString) => {
      const birthDate = new Date(dateString);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age;
    };

    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.father_name) newErrors.father_name = "Father name is required";
    // The radios must not default to "Yes" — the applicant has to pick one explicitly,
    // otherwise father_alive stays undefined and null gets saved.
    if (formData.father_alive !== true && formData.father_alive !== false) {
      newErrors.father_alive = "Please select whether your father is alive";
    }
    if (!fatherAlive && !formData.primary_supporter) newErrors.primary_supporter = "Please select who primarily supports you";
    if (!fatherAlive && !deathCert && !deathCertPreview && !formData.death_certificate?.url) newErrors.death_certificate = "Death certificate is required";
    if (!fatherAlive && formData.primary_supporter === "guardian_relative" && !frcFile && !frcPreview && !formData.proof_of_relationship?.url) newErrors.proof_of_relationship = "Proof of relationship (FRC) is required";
    if (!fatherAlive && !formData.gaurdian_name) newErrors.gaurdian_name = "Guardian name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = "Date of Birth is required";
    } else if (calculateAge(formData.date_of_birth) < 15) {
      newErrors.date_of_birth = "You must be at least 15 years old.";
    }
    if (!formData.applicant_cnic) {
      newErrors.applicant_cnic = "Applicant CNIC is required";
    } else if (!/^\d{13}$/.test(formData.applicant_cnic)) {
      newErrors.applicant_cnic = "Applicant CNIC is invalid. It should be a 13-digit number.";
    }
    if (!formData.parent_cnic) {
      newErrors.parent_cnic = "Parent CNIC is required";
    } else if (!/^\d{13}$/.test(formData.parent_cnic)) {
      newErrors.parent_cnic = "Parent CNIC is invalid. It should be a 13-digit number.";
    }
    if (formData.applicant_cnic && formData.parent_cnic && formData.applicant_cnic === formData.parent_cnic) {
      newErrors.applicant_cnic = "Applicant CNIC and Parent CNIC cannot be the same.";
      newErrors.parent_cnic = "Applicant CNIC and Parent CNIC cannot be the same.";
    }
    if (!formData.province) newErrors.province = "Province is required";
    if (!formData.city) newErrors.city = "City is required";
    if (cityIsOther && !formData.city_other) newErrors.city_other = "Please enter your city";
    if (!formData.permenent_address) newErrors.permenent_address = "Permanent Address is required";
    if (!sameAddress) {
      if (!formData.temp_province) newErrors.temp_province = "Temporary Province is required";
      if (!formData.temp_city) newErrors.temp_city = "Temporary City is required";
      if (tempCityIsOther && !formData.temp_city_other) newErrors.temp_city_other = "Please enter your city";
      if (!formData.temporary_address) newErrors.temporary_address = "Temporary Address is required";
    }
    if (!formData.phone_number) {
      newErrors.phone_number = "Phone number is required";
    } else if (!/^(\+923|03)\d{9}$/.test(formData.phone_number)) {
      newErrors.phone_number = "Phone number is invalid.";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid.";
    }
    if (!profileImage && !profileImagePreview) newErrors.profile_picture = "Profile picture is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async () => {
    if (isDisabled) return;
    if (status === "new") { navigate("/application/academic-information"); return; }
    if (!validateForm()) return;
    setSaving(true);

    const subFormData = new FormData();
    subFormData.append(
      "data",
      JSON.stringify({
        name: formData.name,
        father_name: formData.father_name,
        father_alive: formData.father_alive ?? null,
        primary_supporter: fatherAlive ? "father" : (formData.primary_supporter || ""),
        relationship_detail: (!fatherAlive && formData.primary_supporter === "guardian_relative") ? (frcText || "") : "",
        gaurdian_name: fatherAlive ? "" : formData.gaurdian_name,
        applicant_cnic: formData.applicant_cnic,
        parent_cnic: formData.parent_cnic,
        gender: formData.gender,
        date_of_birth: formData.date_of_birth,
        province: formData.province,
        city: cityIsOther ? formData.city_other : formData.city,
        permenent_address: formData.permenent_address,
        temporary_address: sameAddress ? formData.permenent_address : formData.temporary_address,
        temp_province: sameAddress ? formData.province : formData.temp_province,
        temp_city: sameAddress ? (cityIsOther ? formData.city_other : formData.city) : (tempCityIsOther ? formData.temp_city_other : formData.temp_city),
        phone_number: formData.phone_number,
        email: formData.email,
        application_status: "incomplete",
      })
    );
    if (profileImage) {
      subFormData.append("files.profile_picture", profileImage);
    }
    if (deathCert) {
      subFormData.append("files.death_certificate", deathCert);
    }
    if (frcFile) {
      subFormData.append("files.proof_of_relationship", frcFile);
    }

    try {
      let response;
      if (application) {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/applications/${applicationid}`,
          { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body: subFormData }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/applications`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: subFormData }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData?.error?.message || "Failed to submit form";
        if (errorMsg === "The provided CNIC already exists for this campaign.") {
          NotificationManager.error("The provided CNIC already exists for this campaign.");
        }
        return;
      }

      const data = await response.json();
      if (data?.data?.id) {
        localStorage.setItem("application_id", data.data.id);
        localStorage.setItem("id", data.data.id);
      } else {
        localStorage.setItem("application_id", data.id);
        localStorage.setItem("id", data.data.id);
      }

      // Upload profile image separately if new
      if (profileImage && !application) {
        const imgForm = new FormData();
        imgForm.append("files", profileImage);
        await fetch(`${process.env.REACT_APP_BASE_URL_strapi}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: imgForm,
        });
      }

      setSaving(false);
      navigate("/application/academic-information");
    } catch (error) {
      console.error("Error submitting form:", error);
      setSaving(false);
    }
  };

  // ---- genders (existing values kept) ----
  const genders = [
    { id: 1, name: "Male" },
    { id: 2, name: "Female" },
  ];

  // ---- cities by province (matches reference HTML) ----
  const CITIES = {
    Punjab: ["Gujrat", "Gujranwala", "Lahore", "Rawalpindi", "Faisalabad", "Multan", "Sialkot", "Bahawalpur", "Sargodha"],
    Sindh: ["Hyderabad", "Karachi", "Larkana", "Mirpur Khas", "Sukkur"],
    Kpk: ["Abbottabad", "Kohat", "Mardan", "Peshawar", "Swat"],
    Balochistan: ["Gwadar", "Khuzdar", "Quetta", "Turbat"],
    Islamabad: ["Islamabad"],
    "Gilgit-Baltistan": ["Gilgit", "Hunza", "Skardu"],
    Ajk: ["Kotli", "Mirpur", "Muzaffarabad"],
  };

  const PROVINCES = [
    "Punjab", "Sindh", "Kpk", "Balochistan",
    "Islamabad", "Gilgit-Baltistan", "Ajk",
  ];

  // derived state for conditional fields
  const fatherAlive = formData?.father_alive !== false;
  const cityIsOther = formData?.city === "Other";
  const tempCityIsOther = formData?.temp_city === "Other";
  const sameAddress = formData?.same_address === "yes" || formData?.same_address === true;
  const provinceCities = CITIES[formData?.province] || [];
  const tempProvinceCities = CITIES[formData?.temp_province] || [];

  // ---- helper: field error class ----
  const fieldClass = (field) => `pi__field${errors[field] ? " invalid" : ""}`;

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="pi" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Form */}
      <div className="pi">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* page heading */}
            <h2 className="pi__title">Personal information</h2>
            <p className="pi__sub">Please enter details exactly as written on your CNIC / B-Form.</p>
            <p className="pi__req-note">Fields marked <em className="pi__req">*</em> are required.</p>

            {/* ---- Profile photo ---- */}
            <div className="pi__card">
              <div className={fieldClass("profile_picture")} style={{ marginBottom: 0 }}>
                <div className="pi__photo">
                  <input type="file" accept="image/jpeg,image/png" onChange={handleImageChange} style={{ display: "none" }} id="profileImageInput" disabled={isDisabled} />
                  <label htmlFor="profileImageInput" className="pi__avatar" id="avatar">
                    {profileImagePreview ? (
                      <img src={profileImagePreview} alt="Profile" />
                    ) : (
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
                      </svg>
                    )}
                    <span className="pi__cam">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
                        <circle cx="12" cy="13" r="3.5" />
                      </svg>
                    </span>
                  </label>
                  <div className="pi__photo-copy">
                    <span className="pi__label" style={{ margin: 0 }}>Profile photo <em className="pi__req">*</em></span>
                    <p className="pi__hint" style={{ margin: "2px 0 0" }}>Clear face photo. JPG or PNG, max 5 MB.</p>
                    <label htmlFor="profileImageInput" className="pi__photo-btn">Upload photo</label>
                  </div>
                </div>
                {errors.profile_picture && <p className="pi__err" style={{ gridColumn: "1/-1" }}>{errors.profile_picture}</p>}
              </div>
            </div>

            {/* ---- Basic Information ---- */}
            <div className="pi__card">
              <h3>Basic information</h3>

              <div className="pi__row2">
                <div className={fieldClass("name")}>
                  <label className="pi__label">Name of applicant <em className="pi__req">*</em></label>
                  <input className="pi__input" name="name" placeholder="Full name" value={formData?.name || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.name}</p>
                </div>
                <div className={fieldClass("father_name")}>
                  <label className="pi__label">Father's name <em className="pi__req">*</em></label>
                  <input className="pi__input" name="father_name" placeholder="Father's full name" value={formData?.father_name || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.father_name}</p>
                </div>
              </div>

              {/* Is your father alive? */}
              <div className={fieldClass("father_alive")}>
                <div className="pi__lbl">Is your father alive? <em className="pi__req">*</em></div>
                <div className="pi__seg">
                  <label>
                    <input type="radio" name="father_alive" value="yes" checked={formData.father_alive === true} onChange={() => handleFatherAliveChange(true)} disabled={isDisabled} />
                    <span>Yes</span>
                  </label>
                  <label>
                    <input type="radio" name="father_alive" value="no" checked={formData.father_alive === false} onChange={() => handleFatherAliveChange(false)} disabled={isDisabled} />
                    <span>No</span>
                  </label>
                </div>
                <p className="pi__err">{errors.father_alive}</p>
              </div>

              {/* Death certificate — only when father not alive */}
              {!fatherAlive && (
                <div className={fieldClass("death_certificate")}>
                  <label className="pi__label">Death Certificate <em className="pi__req">*</em></label>
                  <div className="pi__file-upload">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,application/pdf"
                      onChange={handleDeathCertChange}
                      style={{ display: "none" }}
                      id="deathCertInput"
                      disabled={isDisabled}
                    />
                    <label htmlFor="deathCertInput" className="pi__file-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload Death Certificate
                    </label>
                    {deathCertPreview && (
                      <div className="pi__file-preview">
                        {deathCertPreview === "pdf" ? (
                          <span className="pi__file-name">📄 PDF uploaded</span>
                        ) : (
                          <img src={deathCertPreview} alt="Death Certificate" className="pi__file-thumb" />
                        )}
                        {!isDisabled && (
                          <button
                            type="button"
                            className="pi__file-remove"
                            onClick={() => { setDeathCert(null); setDeathCertPreview(null); }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <p className="pi__hint">Upload father's death certificate. JPG, PNG, or PDF, max 5 MB.</p>
                  <p className="pi__err">{errors.death_certificate}</p>
                </div>
              )}

              {/* Primary supporter — only when father not alive */}
              {!fatherAlive && (
                <div className={fieldClass("primary_supporter")}>
                  <label className="pi__label">Who primarily supports your household/education? <em className="pi__req">*</em></label>
                  <select className="pi__input" name="primary_supporter" value={formData?.primary_supporter || ""} onChange={handleChange} disabled={isDisabled}>
                    <option value="">-- Select --</option>
                    <option value="mother">Mother</option>
                    <option value="guardian_relative">Guardian/Relative</option>
                  </select>
                  <p className="pi__err">{errors.primary_supporter}</p>
                </div>
              )}

              {/* Guardian/Mother name — only when father not alive */}
              {!fatherAlive && (
                <div className={fieldClass("gaurdian_name")}>
                  <label className="pi__label">
                    {formData.primary_supporter === "mother" ? "Mother's name" : "Guardian's name"} <em className="pi__req">*</em>
                  </label>
                  <input
                    className="pi__input"
                    name="gaurdian_name"
                    placeholder={formData.primary_supporter === "mother" ? "Mother's full name" : "Guardian's full name"}
                    value={formData?.gaurdian_name || ""}
                    onChange={handleChange}
                    disabled={isDisabled}
                  />
                  <p className="pi__err">{errors.gaurdian_name}</p>
                </div>
              )}

              {/* Proof of Relationship (FRC) — only when Guardian/Relative selected */}
              {!fatherAlive && formData.primary_supporter === "guardian_relative" && (
                <div className={fieldClass("proof_of_relationship")}>
                  <label className="pi__label">Proof of Relationship (FRC) <em className="pi__req">*</em></label>
                  <p className="pi__hint" style={{ marginBottom: "10px" }}>Upload your FCNIC/FRC showing relative's details. JPG, PNG, or PDF, max 5 MB.</p>
                  <div className="pi__file-upload">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,application/pdf"
                      onChange={handleFrcChange}
                      style={{ display: "none" }}
                      id="frcInput"
                      disabled={isDisabled}
                    />
                    <label htmlFor="frcInput" className="pi__file-btn">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload FRC (Family Registration Certificate)
                    </label>
                    {frcPreview && (
                      <div className="pi__file-preview">
                        {frcPreview === "pdf" ? (
                          <span className="pi__file-name">📄 PDF uploaded</span>
                        ) : (
                          <img src={frcPreview} alt="FRC" className="pi__file-thumb" />
                        )}
                        {!isDisabled && (
                          <button
                            type="button"
                            className="pi__file-remove"
                            onClick={() => { setFrcFile(null); setFrcPreview(null); }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <textarea
                    className="pi__textarea"
                    placeholder="Write any additional details about your relationship…"
                    value={frcText}
                    onChange={(e) => setFrcText(e.target.value)}
                    disabled={isDisabled}
                    rows={3}
                    style={{ marginTop: "12px", minHeight: "80px" }}
                  />
                  <p className="pi__err">{errors.proof_of_relationship}</p>
                </div>
              )}

              <div className="pi__row2">
                <div className={fieldClass("applicant_cnic")}>
                  <label className="pi__label">Your CNIC / B-Form no. <em className="pi__req">*</em></label>
                  <input className="pi__input" name="applicant_cnic" inputMode="numeric" placeholder="12345-1234567-1" maxLength={15} value={formData?.applicant_cnic || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.applicant_cnic}</p>
                </div>
                <div className={fieldClass("parent_cnic")}>
                  <label className="pi__label">{fatherAlive ? "Father's CNIC" : "Guardian's CNIC"} <em className="pi__req">*</em></label>
                  <input className="pi__input" name="parent_cnic" inputMode="numeric" placeholder="12345-1234567-1" maxLength={15} value={formData?.parent_cnic || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.parent_cnic}</p>
                </div>
              </div>

              <div className="pi__row2">
                <div className={fieldClass("gender")}>
                  <div className="pi__lbl">Gender <em className="pi__req">*</em></div>
                  <div className="pi__seg">
                    {genders.map((g) => (
                      <label key={g.id}>
                        <input type="radio" name="gender" value={g.name} checked={formData?.gender === g.name} onChange={handleChange} disabled={isDisabled} />
                        <span>{g.name === "PNTS" ? "Prefer Not To Say" : g.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="pi__err">{errors.gender}</p>
                </div>
                <div className={fieldClass("date_of_birth")}>
                  <label className="pi__label">Date of birth <em className="pi__req">*</em></label>
                  <input type="date" className="pi__input" name="date_of_birth" value={formData?.date_of_birth || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.date_of_birth}</p>
                </div>
              </div>
            </div>

            {/* ---- Address ---- */}
            <div className="pi__card">
              <h3>Address</h3>

              <div className="pi__row2">
                <div className={fieldClass("province")}>
                  <label className="pi__label">Province <em className="pi__req">*</em></label>
                  <select className="pi__select" name="province" value={formData?.province || ""} onChange={handleChange} disabled={isDisabled}>
                    <option value="">Select province</option>
                    {PROVINCES.map((p) => (<option key={p} value={p}>{p}</option>))}
                  </select>
                  <p className="pi__err">{errors.province}</p>
                </div>
                <div className={fieldClass("city")}>
                  <label className="pi__label">City <em className="pi__req">*</em></label>
                  <select className="pi__select" name="city" value={formData?.city || ""} onChange={handleChange} disabled={isDisabled || !formData?.province}>
                    <option value="">Select city</option>
                    {provinceCities.map((c) => (<option key={c} value={c}>{c}</option>))}
                    <option value="Other">Other</option>
                  </select>
                  <p className="pi__err">{errors.city}</p>
                </div>
              </div>

              {/* Custom city — only when city = Other */}
              {cityIsOther && (
                <div className={fieldClass("city_other")}>
                  <label className="pi__label">Your city <em className="pi__req">*</em></label>
                  <input className="pi__input" name="city_other" placeholder="Type your city" value={formData?.city_other || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.city_other}</p>
                </div>
              )}

              <div className={fieldClass("permenent_address")}>
                <label className="pi__label">Permanent address <em className="pi__req">*</em></label>
                <input className="pi__input" name="permenent_address" placeholder="House, street, area" value={formData?.permenent_address || ""} onChange={handleChange} disabled={isDisabled} />
                <p className="pi__err">{errors.permenent_address}</p>
              </div>

              {/* Same address checkbox */}
              <div className="pi__field">
                <label className="pi__check">
                  <input type="checkbox" name="same_address" checked={sameAddress} onChange={(e) => {
                    handleChange({ target: { name: "same_address", value: e.target.checked ? "yes" : "no" } });
                  }} disabled={isDisabled} />
                  <span>Temporary address is the same as permanent</span>
                </label>
              </div>

              {/* Temporary address — only when different */}
              {!sameAddress && (
                <>
                  <div className="pi__row2">
                    <div className={fieldClass("temp_province")}>
                      <label className="pi__label">Temporary Province <em className="pi__req">*</em></label>
                      <select className="pi__select" name="temp_province" value={formData?.temp_province || ""} onChange={handleChange} disabled={isDisabled}>
                        <option value="">Select province</option>
                        {PROVINCES.map((p) => (<option key={p} value={p}>{p}</option>))}
                      </select>
                      <p className="pi__err">{errors.temp_province}</p>
                    </div>
                    <div className={fieldClass("temp_city")}>
                      <label className="pi__label">Temporary City <em className="pi__req">*</em></label>
                      <select className="pi__select" name="temp_city" value={formData?.temp_city || ""} onChange={handleChange} disabled={isDisabled || !formData?.temp_province}>
                        <option value="">Select city</option>
                        {tempProvinceCities.map((c) => (<option key={c} value={c}>{c}</option>))}
                        <option value="Other">Other</option>
                      </select>
                      <p className="pi__err">{errors.temp_city}</p>
                    </div>
                  </div>

                  {/* Custom temporary city — only when temp_city = Other */}
                  {tempCityIsOther && (
                    <div className={fieldClass("temp_city_other")}>
                      <label className="pi__label">Your city <em className="pi__req">*</em></label>
                      <input className="pi__input" name="temp_city_other" placeholder="Type your city" value={formData?.temp_city_other || ""} onChange={handleChange} disabled={isDisabled} />
                      <p className="pi__err">{errors.temp_city_other}</p>
                    </div>
                  )}

                  <div className={fieldClass("temporary_address")}>
                    <label className="pi__label">Temporary address <em className="pi__req">*</em></label>
                    <input className="pi__input" name="temporary_address" placeholder="Where you live now" value={formData?.temporary_address || ""} onChange={handleChange} disabled={isDisabled} />
                    <p className="pi__err">{errors.temporary_address}</p>
                  </div>
                </>
              )}
            </div>

            {/* ---- Contact Information ---- */}
            <div className="pi__card">
              <h3>Contact Information</h3>

              <div className="pi__row2">
                <div className={fieldClass("phone_number")}>
                  <label className="pi__label">Mobile Number <em className="pi__req">*</em></label>
                  <input className="pi__input" name="phone_number" placeholder="03000000000" value={formData?.phone_number || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.phone_number}</p>
                </div>
                <div className={fieldClass("email")}>
                  <label className="pi__label">Email <em className="pi__req">*</em></label>
                  <input type="email" className="pi__input" name="email" placeholder="name@example.com" value={formData?.email || ""} onChange={handleChange} disabled={isDisabled} />
                  <p className="pi__err">{errors.email}</p>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/instructions")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        hideBack={false}
        nextLoading={saving}
        nextDisabled={isDisabled}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default PersonalInformation;
